import { useEffect, useMemo, useReducer } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  IconButton,
  Input,
  Tooltip,
} from '@airbus/components-react';
import { Close, Search } from '@airbus/icons/react';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import { AttributeGroup } from '../../types';
import {
  resolveBulkDataType,
  type BulkAttributeConfig,
  type BulkAttributeState,
  type BulkAttributeValue,
} from './BulkUpdateInputRenderer';
import {
  hasValidationError,
  validateAndBuildUpdates,
} from '../../services/bulkUpdate/bulkUpdateValidation.ts';
import BulkUpdateAttributeGroups from './BulkUpdateAttributeGroups';
import BulkUpdateConfirmDialog from './BulkUpdateConfirmDialog.tsx';
import {
  BulkUpdateDialogActions,
  bulkUpdateDialogReducer,
  createInitialBulkUpdateDialogState,
} from './BulkUpdateDialogStateMachine';
import styles from './BulkUpdateDialog.module.css';

type BulkUpdateDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (
    updates: Array<{ attrInternalName: string; value: BulkAttributeValue }>,
  ) => Promise<void> | void;
  rowsToUpdate: number;
  rowLimitExceeded: boolean;
  maxRowsToUpdate: number;
  cachedState: BulkUpdateDialogCacheState | null;
  onStateCacheChange: (state: BulkUpdateDialogCacheState) => void;
  attributes: BulkAttributeConfig[];
  attributeGroups: AttributeGroup[];
  title?: string;
};

export type BulkUpdateDialogCacheState = {
  search: string;
  attributesState: Record<string, BulkAttributeState>;
  collapsedGroups: Array<{ groupId: number; collapsed: boolean }>;
  hasAttemptedSave: boolean;
};

function getLocalizedLabel(attribute: BulkAttributeConfig, language: string) {
  return language === 'de' ? attribute.attrDisplayNamede : attribute.attrDisplayNameen;
}

function getInitialValueByType(attribute: BulkAttributeConfig): BulkAttributeValue {
  const type = resolveBulkDataType(attribute);
  if (type === 'date') return null;
  return '';
}

export default function BulkUpdateDialog({
  open,
  onClose,
  onSave,
  rowsToUpdate,
  rowLimitExceeded,
  maxRowsToUpdate,
  cachedState,
  onStateCacheChange,
  attributes,
  attributeGroups,
  title = 'Bulk Update',
}: BulkUpdateDialogProps) {
  const { language, t } = useLanguageContextAPI();

  const [dialogState, dispatch] = useReducer(
    bulkUpdateDialogReducer,
    createInitialBulkUpdateDialogState(),
  );

  const buildAttributesState = (sourceAttributes: BulkAttributeConfig[]) => {
    const nextStates: Record<string, BulkAttributeState> = {};
    sourceAttributes.forEach((attribute) => {
      nextStates[attribute.attrInternalName] = {
        enabled: false,
        value: getInitialValueByType(attribute),
        error: false,
      };
    });

    return nextStates;
  };

  // Use cached values if available; otherwise use defaults.
  const buildMergedAttributesState = (
    sourceAttributes: BulkAttributeConfig[],
    cachedAttributesState?: Record<string, BulkAttributeState>,
  ) => {
    const defaultStates = buildAttributesState(sourceAttributes);
    const mergedStates: Record<string, BulkAttributeState> = {};

    sourceAttributes.forEach((attribute) => {
      const attrName = attribute.attrInternalName;
      const cachedAttributeState = cachedAttributesState?.[attrName];

      mergedStates[attrName] = cachedAttributeState
        ? { ...defaultStates[attrName], ...cachedAttributeState }
        : defaultStates[attrName];
    });

    return mergedStates;
  };

  useEffect(() => {
    if (!open) return;

    const nextStates = buildMergedAttributesState(attributes, cachedState?.attributesState);

    const cachedCollapsedByGroupId = new Map(
      (cachedState?.collapsedGroups ?? []).map((group) => [group.groupId, group.collapsed]),
    );

    const nextCollapsedGroups = attributeGroups.map((group) => ({
      groupId: group.id,
      collapsed: cachedCollapsedByGroupId.get(group.id) ?? false,
    }));

    dispatch(
      BulkUpdateDialogActions.resetDialog({
        attributesState: nextStates,
        collapsedGroups: nextCollapsedGroups,
      }),
    );

    dispatch(BulkUpdateDialogActions.setSearch(cachedState?.search ?? ''));
    dispatch(BulkUpdateDialogActions.setHasAttemptedSave(!!cachedState?.hasAttemptedSave));
  }, [open]);

  useEffect(() => {
    if (!open || !onStateCacheChange) return;

    onStateCacheChange({
      search: dialogState.search,
      attributesState: dialogState.attributesState,
      collapsedGroups: dialogState.collapsedGroups,
      hasAttemptedSave: dialogState.hasAttemptedSave,
    });
  }, [
    open,
    onStateCacheChange,
    dialogState.search,
    dialogState.attributesState,
    dialogState.collapsedGroups,
    dialogState.hasAttemptedSave,
  ]);

  const visibleAttributes = useMemo(() => {
    const query = dialogState.search.trim().toLowerCase();

    return attributes.filter((attribute) => {
      if (!query) return true;

      const displayName = getLocalizedLabel(attribute, language).toLowerCase();
      const internalName = attribute.attrInternalName.toLowerCase();
      return displayName.includes(query) || internalName.includes(query);
    });
  }, [attributes, dialogState.search, language]);

  const groupedAttributes = useMemo(
    () =>
      attributeGroups.map((group) => ({
        group,
        groupId: group.id,
        items: visibleAttributes.filter((attribute) => attribute.attrGroupId === group.id),
      })),
    [attributeGroups, visibleAttributes],
  );

  const hasAtLeastOneValidChange = useMemo(
    () =>
      attributes.some((attribute) => {
        const state = dialogState.attributesState[attribute.attrInternalName];
        if (!state?.enabled) return false;
        return !hasValidationError(attribute, state);
      }),
    [dialogState.attributesState, attributes],
  );

  const saveDisabled = dialogState.isSubmitting || !hasAtLeastOneValidChange || rowLimitExceeded;

  const openConfirmDialog = () => {
    if (rowLimitExceeded || dialogState.isSubmitting) return;

    const { validatedStates, hasError, updates } = validateAndBuildUpdates({
      attributes,
      sourceStates: dialogState.attributesState,
      getInitialValueByType,
    });

    if (hasError || updates.length === 0) {
      dispatch(BulkUpdateDialogActions.replaceAttributeStates(validatedStates));
      dispatch(BulkUpdateDialogActions.setHasAttemptedSave(true));
      dispatch(BulkUpdateDialogActions.setConfirmOpen(false));
      return;
    }

    dispatch(
      BulkUpdateDialogActions.prepareConfirmation({
        validatedStates,
        updates,
      }),
    );
  };

  const closeConfirmDialog = () => {
    dispatch(BulkUpdateDialogActions.setConfirmOpen(false));
  };

  const handleAttributeCheckboxToggle = (attribute: BulkAttributeConfig, checked: boolean) => {
    const previous = dialogState.attributesState[attribute.attrInternalName] ?? {
      enabled: false,
      value: getInitialValueByType(attribute),
      error: false,
    };

    const nextState: BulkAttributeState = {
      ...previous,
      enabled: checked,
    };

    if (!checked) {
      nextState.value = getInitialValueByType(attribute);
      nextState.error = false;
    } else {
      // Newly enabled fields should start clean; validate on next save or value change.
      nextState.error = false;
    }

    dispatch(
      BulkUpdateDialogActions.upsertAttributeState({
        attrInternalName: attribute.attrInternalName,
        attributeState: nextState,
      }),
    );
  };

  const handleValueChange = (attribute: BulkAttributeConfig, value: BulkAttributeValue) => {
    const previous = dialogState.attributesState[attribute.attrInternalName] ?? {
      enabled: false,
      value: getInitialValueByType(attribute),
      error: false,
    };

    const nextState = {
      ...previous,
      value,
    };

    nextState.error = dialogState.hasAttemptedSave
      ? hasValidationError(attribute, nextState)
      : false;

    dispatch(
      BulkUpdateDialogActions.upsertAttributeState({
        attrInternalName: attribute.attrInternalName,
        attributeState: nextState,
      }),
    );
  };

  const handleGroupCollapseToggle = (groupId: number) => {
    dispatch(BulkUpdateDialogActions.toggleCollapsedGroup(groupId));
  };

  const clearAll = () => {
    if (dialogState.isSubmitting) return;

    const resetState = buildAttributesState(attributes);
    dispatch(BulkUpdateDialogActions.clearAll(resetState));
    dispatch(BulkUpdateDialogActions.setSearch(''));
  };

  const handleSave = async () => {
    const updates = dialogState.pendingUpdates;
    if (!updates.length) return;

    try {
      dispatch(BulkUpdateDialogActions.setSubmitting(true));
      await onSave(updates);
    } finally {
      dispatch(BulkUpdateDialogActions.setSubmitting(false));
      dispatch(BulkUpdateDialogActions.setConfirmOpen(false));
    }
  };
  const saveButton = (
    <Button variant='primary' onClick={openConfirmDialog} disabled={saveDisabled}>
      {t('save')}
    </Button>
  );

  const saveButtonWithOptionalLimitTooltip = rowLimitExceeded ? (
    <Tooltip label={t('bulkUpdate.limitExceededHint', { limit: maxRowsToUpdate })}>
      {saveButton}
    </Tooltip>
  ) : (
    saveButton
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        className={styles.dialog}
        style={{
          width: '600px',
          maxWidth: '1904px',
          maxHeight: '682px',
          overflow: 'hidden',
        }}
      >
        <DialogHeader>{title}</DialogHeader>
        <DialogContent className={styles.dialogContent}>
          <div className={styles.headerControls}>
            <div className={styles.rowsToUpdate}>
              {t('bulkUpdate.targetRowsLabel')}: {rowsToUpdate}
            </div>
            <Input
              placeholder='Search'
              value={dialogState.search}
              onChange={(event) => dispatch(BulkUpdateDialogActions.setSearch(event.target.value))}
              startAdornment={<Search />}
              endAdornment={
                dialogState.search.length > 0 ? (
                  <IconButton
                    variant='ghost'
                    size='small'
                    onClick={() => dispatch(BulkUpdateDialogActions.setSearch(''))}
                    aria-label='clear'
                  >
                    <Close className={styles.closeButton} />
                  </IconButton>
                ) : null
              }
            />
          </div>

          <BulkUpdateAttributeGroups
            visibleAttributesCount={visibleAttributes.length}
            groupedAttributes={groupedAttributes}
            collapsedGroups={dialogState.collapsedGroups}
            language={language}
            attributesState={dialogState.attributesState}
            getInitialValueByType={getInitialValueByType}
            onGroupCollapseToggle={handleGroupCollapseToggle}
            onAttributeCheckboxToggle={handleAttributeCheckboxToggle}
            onValueChange={handleValueChange}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='secondary' onClick={clearAll} disabled={dialogState.isSubmitting}>
            {t('clear_all')}
          </Button>
          <Button variant='secondary' onClick={onClose} disabled={dialogState.isSubmitting}>
            {t('cancel')}
          </Button>
          {saveButtonWithOptionalLimitTooltip}
        </DialogActions>
      </Dialog>

      <BulkUpdateConfirmDialog
        open={dialogState.isConfirmOpen}
        title={t('bulkUpdate.confirmTitle')}
        message={t('bulkUpdate.warningMessage')}
        confirmLabel={t('bulkUpdate.confirm')}
        quitLabel={t('bulkUpdate.quit')}
        isSubmitting={dialogState.isSubmitting}
        onConfirm={handleSave}
        onClose={closeConfirmDialog}
      />
    </>
  );
}
